import React, {FC, useState} from "react"

import Flex from "@irati/ui/src/Flex"
import {Body, H3} from "@irati/ui/src/Typography"
import FormattedMessage, {injectIntl, InjectIntl} from "@irati/utils/lang"
import GenericModal from "@irati/ui/src/GenericModal"
import Button from "@irati/ui/src/Button"
import Selector from "@irati/ui/src/Selector"
import adminTracking from "@irati-tracking/admin"
import Spacing from "@irati/ui/src/Spacing"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import theme from "@irati/ui/src/global/theme"

import {SelectorWrapper} from "../Integrations/styled"
import {InfoCard} from "../../Settings/Integration/styled"
import {ONBOARDING_STEPS, Reason} from "./Steps"

interface Props extends InjectIntl {
  show: boolean
  reasons: Reason[]
  sourceEvent: string
  onHide: () => void
}

const OnboardingFeedbackModal: FC<Props> = ({show, reasons, sourceEvent, onHide, formatMessage}) => {
  const [selectedReason, setSelectedReason] = useState<string>("")
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false)
  const [hint, setHint] = useState<string>()

  const fixedWidth = sourceEvent === ONBOARDING_STEPS[0].event || sourceEvent === ONBOARDING_STEPS[3].event ? true : (false as boolean)

  const onSelect = (_, reason: string) => {
    const reasonEvent = reasons.find((item) => formatMessage({id: item.key}) === reason)?.event
    if (reasonEvent) {
      setSelectedReason(reasonEvent)
    }
  }

  const handleConfirm = () => {
    adminTracking.submitOnboardingV2Feedback(sourceEvent, selectedReason)
    const reasonHint = reasons.find((item) => selectedReason === item.event)?.hint
    if (reasonHint) {
      setHint(reasonHint)
    }
    handleHide()
    setShowConfirmation(true)
  }

  const handleHide = () => {
    setSelectedReason("")
    onHide()
  }

  const handleConfirmationHide = () => {
    setHint("")
    setShowConfirmation(false)
  }
  return (
    <>
      <GenericModal customWidth={fixedWidth ? 800 : null} show={show} hideActions onHide={handleHide}>
        <Flex direction="column" justifyContent="space-between">
          <Flex direction="column">
            <H3 marginBottom>
              <FormattedMessage id="admin.components.onboardingProgress.setup.reasonModal.title" />
            </H3>
            <Body marginBottom={2}>
              <FormattedMessage id="admin.components.onboardingProgress.setup.reasonModal.subTitle" />
            </Body>
            <SelectorWrapper>
              {reasons.map((item: Reason, i: number) => (
                <Selector
                  key={i}
                  name={formatMessage({id: item.key})}
                  onClick={onSelect}
                  id="adminSection_onboardingStep_feedbackReason"
                  selected={selectedReason === item.event}
                />
              ))}
            </SelectorWrapper>
          </Flex>
          <Flex marginTop justifyContent="flex-start">
            <Button id="adminSection_onboardingModalConfirm_button" primary onClick={handleConfirm} disabled={!selectedReason}>
              <FormattedMessage id="admin.components.onboardingProgress.setup.reasonModal.confirmBtn" />
            </Button>
          </Flex>
        </Flex>
      </GenericModal>
      <GenericModal show={showConfirmation} hideActions onHide={handleConfirmationHide}>
        <Flex direction="column" justifyContent="space-between">
          <Flex direction="column">
            <H3 marginBottom>
              <FormattedMessage id="admin.components.onboardingProgress.setup.reasonModal.confirmation" />
            </H3>
            {hint && (
              <InfoCard padding={2} marginBottom={1}>
                <Spacing>
                  <Icon color={theme.colors.tertiary.g_400} icon={IconTypes.circleAlert} size={20} />
                </Spacing>
                <Body marginLeft={1}>
                  <FormattedMessage id={hint} />
                </Body>
              </InfoCard>
            )}
          </Flex>
          <Flex marginTop={2} justifyContent="flex-start">
            <Button id="adminSection_onboardingModalOk_button" primary onClick={handleConfirmationHide}>
              <FormattedMessage id="admin.containers.globalModal.finalStepButton" />
            </Button>
          </Flex>
        </Flex>
      </GenericModal>
    </>
  )
}

export default injectIntl(OnboardingFeedbackModal)
